import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Megaphone, Eye, Check, Tag as TagIcon, Users as UsersIcon, Crown } from "lucide-react";

const SEGMENTS = [
  { value: "all", label: "Všetci / All", icon: UsersIcon },
  { value: "subscribers", label: "Aktívni subscribers", icon: Crown },
  { value: "nsfw_unlocked", label: "NSFW odomknutí", icon: null },
  { value: "top_spenders", label: "Top spenders", icon: Crown },
  { value: "tag", label: "Podľa tagu", icon: TagIcon },
  { value: "manual", label: "Manuálny výber", icon: Check },
];

const PLATFORMS = [
  { value: "__all", label: "Všetky platformy" },
  { value: "fanvue", label: "Fanvue" },
  { value: "telegram", label: "Telegram" },
];

function Schedule() {
  const MAX_JOBS = 5;
  const emptyRow = () => ({ time: "09:00", text: "", segment: "active", photo_id: "", photo_free: false, photo_price: "" });
  const [jobs, setJobs] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const load = async () => { try { const r = await api.get("/scheduled"); setJobs(r.data || []); } catch (_) {} };
  useEffect(() => {
    load();
    api.get("/photos").then((r) =>
      setPhotos((r.data || []).filter((p) => p.fanvue_media_id || p.fanvue_uuid))
    ).catch(() => {});
  }, []);

  const slotsLeft = Math.max(0, MAX_JOBS - jobs.length);
  const canAddRow = rows.length < slotsLeft;

  const setRow = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const dropRow = (i) => setRows(rows.length === 1 ? [emptyRow()] : rows.filter((_, j) => j !== i));

  const saveAll = async () => {
    const filled = rows.filter((r) => r.text.trim());
    if (!filled.length) { toast.error("Napíš aspoň jednu správu"); return; }
    if (filled.length > slotsLeft) { toast.error(`Limit je ${MAX_JOBS} správ v rozvrhu (voľné: ${slotsLeft})`); return; }
    setSaving(true);
    try {
      for (const r of filled) {
        await api.post("/scheduled", {
          time_of_day: r.time, text: r.text.trim(), segment: r.segment,
          ai_variate: true, enabled: true,
          photo_id: r.photo_id || null,
          photo_free: !!r.photo_free,
          photo_price: r.photo_id && !r.photo_free ? (parseFloat(r.photo_price) || null) : null,
        });
      }
      toast.success(`Naplánované: ${filled.length} ✓`);
      setRows([emptyRow()]); load();
    } catch (e) { toast.error("Uloženie zlyhalo — spusti dash-scheduled.sql"); }
    finally { setSaving(false); }
  };

  const toggle = async (j) => { await api.patch(`/scheduled/${j.id}`, { enabled: !j.enabled }); load(); };
  const del = async (j) => { await api.delete(`/scheduled/${j.id}`); load(); };

  return (
    <div className="mt-10 border border-white/10 p-5">
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-bold text-white mb-1">⏰ Rozvrh správ</div>
        <div className="text-[10px] font-mono text-white/30">{jobs.length}/{MAX_JOBS}</div>
      </div>
      <p className="text-white/40 text-[11px] mb-4">
        Správy odchádzajú každý deň v zadanom čase — v časovom pásme persony z AI nastavení. Beží na serveri,
        aj keď si odhlásený. AI vygeneruje variácie, rozosiela sa postupne (30–120 s rozostupy).
      </p>

      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap gap-2 items-end mb-3">
          <div>
            {i === 0 && <label className="text-[9px] uppercase tracking-widest text-white/40">Čas</label>}
            <input type="time" value={r.time} onChange={(e) => setRow(i, { time: e.target.value })}
              data-testid={`sched-time-${i}`}
              className="block bg-black/40 border border-white/10 px-2 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
          </div>
          <div className="flex-1 min-w-[220px]">
            {i === 0 && <label className="text-[9px] uppercase tracking-widest text-white/40">Správa</label>}
            <input value={r.text} onChange={(e) => setRow(i, { text: e.target.value })}
              placeholder='napr. "just woke up… had such a nice dream about you 😏"'
              data-testid={`sched-text-${i}`}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50 placeholder:text-white/25" />
          </div>
          <div>
            {i === 0 && <label className="text-[9px] uppercase tracking-widest text-white/40">Komu</label>}
            <select value={r.segment} onChange={(e) => setRow(i, { segment: e.target.value })}
              data-testid={`sched-segment-${i}`}
              className="block bg-black/40 border border-white/10 px-2 py-2 text-sm mt-1 outline-none">
              <option value="active" className="bg-black">Aktívni (7 dní)</option>
              <option value="spenders" className="bg-black">Platiaci</option>
              <option value="all" className="bg-black">Všetci</option>
            </select>
          </div>
          <button onClick={() => dropRow(i)} title="Odobrať pole"
            className="px-2 py-2 text-white/30 hover:text-rose-400 text-sm">✕</button>

          {/* 📷 fotka k správe */}
          <div className="w-full flex flex-wrap gap-2 items-center pl-1 -mt-1">
            <select value={r.photo_id} onChange={(e) => setRow(i, { photo_id: e.target.value })}
              data-testid={`sched-photo-${i}`}
              className="bg-black/40 border border-white/10 px-2 py-1.5 text-xs outline-none max-w-[240px]">
              <option value="" className="bg-black">— bez fotky —</option>
              {photos.map((p) => (
                <option key={p.id} value={p.id} className="bg-black">
                  📷 {(p.title || "fotka").slice(0, 28)}{p.price > 0 ? ` ($${Number(p.price).toFixed(0)})` : ""}
                </option>
              ))}
            </select>
            {r.photo_id && (
              <>
                <div className="flex border border-white/10">
                  {[{ v: false, l: "💰 Platená" }, { v: true, l: "🎁 Free" }].map((o) => (
                    <button key={String(o.v)} onClick={() => setRow(i, { photo_free: o.v })}
                      className={`px-2.5 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                        r.photo_free === o.v ? "bg-fuchsia-500/20 text-fuchsia-300" : "text-white/40 hover:text-white/70"
                      }`}>{o.l}</button>
                  ))}
                </div>
                {!r.photo_free && (
                  <input type="number" min="1" step="0.5" value={r.photo_price}
                    onChange={(e) => setRow(i, { photo_price: e.target.value })}
                    placeholder={`$${Number(photos.find((p) => p.id === r.photo_id)?.price || 9.99).toFixed(2)}`}
                    data-testid={`sched-price-${i}`}
                    className="w-20 bg-black/40 border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-fuchsia-500/50 placeholder:text-white/25" />
                )}
                <span className="text-[9px] text-white/25">tomu, kto ju už dostal, pôjde len text</span>
              </>
            )}
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => canAddRow && setRows([...rows, emptyRow()])}
          disabled={!canAddRow}
          data-testid="sched-add-row"
          title={canAddRow ? "Pridať ďalšiu správu" : `Limit ${MAX_JOBS} správ v rozvrhu`}
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-2 border border-white/15 text-white/60 hover:border-fuchsia-500/50 hover:text-fuchsia-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          ＋ ďalšia správa
        </button>
        <button onClick={saveAll} disabled={saving}
          data-testid="sched-save"
          className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest bg-fuchsia-500/15 border border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/25 disabled:opacity-50 transition-colors">
          {saving ? "Ukladám…" : "Uložiť do rozvrhu"}
        </button>
        {!canAddRow && rows.length + jobs.length >= MAX_JOBS && (
          <span className="text-[10px] text-white/30">limit {MAX_JOBS} — zmaž staré, ak chceš pridať</span>
        )}
      </div>

      {jobs.length === 0 ? (
        <div className="text-white/25 text-xs">Žiadne naplánované správy.</div>
      ) : jobs.map((j) => (
        <div key={j.id} className="flex items-center gap-3 border-t border-white/5 py-2.5 text-sm">
          <span className="font-mono text-fuchsia-300 w-14">{j.time_of_day}</span>
          <span className={`flex-1 truncate ${j.enabled ? "text-white/80" : "text-white/30 line-through"}`}>
            {j.photo_id ? (j.photo_free ? "🎁 " : `💰$${Number(j.photo_price || 0).toFixed(0)} `) : ""}{j.text}
          </span>
          <span className="text-[9px] uppercase text-white/30">{j.segment}</span>
          {j.last_sent_date && <span className="text-[9px] text-emerald-400/60">✓ {j.last_sent_date}</span>}
          <button onClick={() => toggle(j)} className={`text-[10px] px-2 py-1 border ${j.enabled ? "border-emerald-500/40 text-emerald-300" : "border-white/10 text-white/40"}`}>
            {j.enabled ? "ON" : "OFF"}
          </button>
          <button onClick={() => del(j)} className="text-white/30 hover:text-rose-400 text-xs">✕</button>
        </div>
      ))}
    </div>
  );
}

export default function MassMessage() {
  const { lang } = useT();
  const [segment, setSegment] = useState("subscribers");
  const [platform, setPlatform] = useState("fanvue");
  const [prompt, setPrompt] = useState(
    "Tell him you just made something new and special — tease him sweetly and invite him to ask for it 💜"
  );
  const [dryRun, setDryRun] = useState(true);
  const [picked, setPicked] = useState(new Set());
  const [sendingPicked, setSendingPicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [fans, setFans] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tag, setTag] = useState("");
  const [minSpend, setMinSpend] = useState("50");
  const [manualSet, setManualSet] = useState(new Set());
  const [fanFilter, setFanFilter] = useState("");

  // Load fans + extract tags
  useEffect(() => {
    api.get("/fans").then((r) => {
      setFans(r.data);
      const tags = new Set();
      r.data.forEach((f) => (f.tags || []).forEach((t) => tags.add(t)));
      setAllTags([...tags].sort());
    });
  }, []);

  const platformFilteredFans = useMemo(() => {
    const list = platform === "__all" || !platform ? fans : fans.filter((f) => f.platform === platform);
    if (!fanFilter) return list;
    const q = fanFilter.toLowerCase();
    return list.filter((f) => f.name.toLowerCase().includes(q));
  }, [fans, platform, fanFilter]);

  const segmentCount = useMemo(() => {
    let list = platform === "__all" || !platform ? fans : fans.filter((f) => f.platform === platform);
    if (segment === "nsfw_unlocked") list = list.filter((f) => f.nsfw_unlocked);
    else if (segment === "subscribers") list = list.filter((f) => f.fan_status === "subscriber");
    else if (segment === "top_spenders") {
      const threshold = parseFloat(minSpend) || 0;
      list = list.filter((f) => (f.spend_total || 0) >= threshold || f.is_top_spender);
    } else if (segment === "tag") list = list.filter((f) => (f.tags || []).includes(tag));
    else if (segment === "manual") list = list.filter((f) => manualSet.has(f.id));
    return list.length;
  }, [fans, platform, segment, tag, minSpend, manualSet]);

  const toggleManual = (id) => {
    const next = new Set(manualSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setManualSet(next);
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const body = {
        segment,
        platform: platform === "__all" ? undefined : platform || undefined,
        prompt,
        dry_run: dryRun,
        max_fans: 100,
      };
      if (segment === "tag") body.tag = tag;
      if (segment === "manual") body.fan_ids = Array.from(manualSet);
      if (segment === "top_spenders") body.min_spend = parseFloat(minSpend) || 50;

      const r = await api.post("/mass-message", body);
      setResult(r.data);
      setPicked(new Set((r.data.drafts || []).map((d) => d.fan_id)));
      if (dryRun) toast.success(`Vygenerovaných ${r.data.drafts?.length || 0} návrhov`);
      else toast.success(
        `Zaradené do fronty: ${r.data.sent}/${r.data.candidates}` +
        (r.data.spread_minutes ? ` — rozložené do ~${r.data.spread_minutes} min` : "") +
        (r.data.failed ? ` (${r.data.failed} zlyhalo)` : ""),
        { duration: 9000 });
    } catch (e) {
      toast.error("Mass message failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const sendPicked = async () => {
    const items = (result?.drafts || [])
      .filter((d) => picked.has(d.fan_id))
      .map((d) => ({ fan_id: d.fan_id, text: d.text }));
    if (!items.length) { toast.error("Nič nie je vybrané"); return; }
    setSendingPicked(true);
    try {
      const r = await api.post("/mass-message/send-drafts", { items });
      toast.success(`Zaradené do fronty: ${r.data.queued} — rozložené do ~${r.data.spread_minutes} min`, { duration: 9000 });
      setResult(null); setPicked(new Set());
    } catch (e) {
      toast.error("Odoslanie zlyhalo: " + (e?.response?.data?.detail || e.message));
    } finally { setSendingPicked(false); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl fade-up">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.35em] text-white/40">Hromadná správa</div>
        <h1 className="font-serif text-5xl mt-2">
          One prompt. <span className="italic text-fuchsia-400">Hundred conversations.</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <Label className="text-base font-serif">Skupina fanúšikov</Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SEGMENTS.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSegment(s.value)}
                  data-testid={`seg-${s.value}`}
                  className={`px-3 py-3 border text-xs uppercase tracking-widest text-left ${
                    segment === s.value
                      ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-200"
                      : "border-white/10 text-white/55 hover:text-white"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Conditional UI per segment */}
          {segment === "top_spenders" && (
            <Card>
              <Label className="text-[10px] uppercase tracking-widest text-white/40">Minimum lifetime spend ($)</Label>
              <div className="text-xs text-white/40 mt-1 mb-2">
                Fanúšikovia ktorí utratili minimálne túto sumu (alebo majú Fanvue „top spender" tag).
              </div>
              <Input
                type="number"
                value={minSpend}
                onChange={(e) => setMinSpend(e.target.value)}
                data-testid="input-min-spend"
                className="bg-transparent border-white/10 rounded-none w-32"
              />
            </Card>
          )}

          {segment === "tag" && (
            <Card>
              <Label className="text-[10px] uppercase tracking-widest text-white/40">Tag</Label>
              <div className="text-xs text-white/40 mt-1 mb-2">
                {allTags.length === 0
                  ? "Ešte nemáš žiadne tagy. Choď do /fans a otaguj fanov tlačidlom +."
                  : "Vyber existujúci tag alebo napíš nový."}
              </div>
              <Input
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="napr. VIP, slovak, kinky"
                list="tag-list"
                data-testid="input-tag"
                className="bg-transparent border-white/10 rounded-none mt-1"
              />
              <datalist id="tag-list">
                {allTags.map((t) => <option key={t} value={t} />)}
              </datalist>
              {allTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {allTags.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTag(t)}
                      className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                        tag === t ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-200" : "border-white/10 text-white/60 hover:text-white"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          )}

          {segment === "manual" && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-serif">Vyber fanúšikov ručne</Label>
                <Input
                  value={fanFilter}
                  onChange={(e) => setFanFilter(e.target.value)}
                  placeholder="hľadať..."
                  className="bg-transparent border-white/10 rounded-none w-40 h-8 text-xs"
                  data-testid="input-fan-filter"
                />
              </div>
              <div className="text-xs text-white/40 mb-3">
                Vybraných: <span className="text-fuchsia-300 font-medium tabular-nums">{manualSet.size}</span> z {platformFilteredFans.length}
                {manualSet.size > 0 && (
                  <button onClick={() => setManualSet(new Set())} className="ml-3 text-white/40 hover:text-rose-400 underline">
                    zrušiť výber
                  </button>
                )}
              </div>
              <div className="border border-white/5 max-h-72 overflow-y-auto">
                {platformFilteredFans.map((f) => {
                  const checked = manualSet.has(f.id);
                  return (
                    <button
                      key={f.id}
                      onClick={() => toggleManual(f.id)}
                      data-testid={`manual-pick-${f.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2 border-b border-white/5 text-left hover:bg-white/[0.03] ${checked ? "bg-fuchsia-500/[0.08]" : ""}`}
                    >
                      <div className={`w-4 h-4 border flex items-center justify-center ${checked ? "border-fuchsia-400 bg-fuchsia-500" : "border-white/20"}`}>
                        {checked && <Check size={11} className="text-white" />}
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-sm">{f.name}</span>
                        <span className="text-[10px] text-white/40 uppercase tracking-widest">{f.platform}</span>
                        <span className="text-xs tabular-nums text-emerald-400 ml-3">${(f.spend_total || 0).toFixed(0)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          <Card>
            <Label className="text-[10px] uppercase tracking-widest text-white/40">Platforma</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="bg-transparent border-white/10 rounded-none mt-2" data-testid="select-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[rgb(18,20,28)] border-white/10">
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>

          <Card>
            <Label className="text-base font-serif">Zámer správy (AI personalizuje pre každého)</Label>
            <div className="text-xs text-white/40 mt-1 mb-3">
              AI prispôsobí tón, jazyk fana, použije jeho meno a poznámky.
            </div>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              className="bg-transparent border-white/10 rounded-none"
              data-testid="textarea-mass-prompt"
            />
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-serif">Náhľad pred odoslaním (dry-run)</Label>
                <div className="text-xs text-white/40 mt-1">ZAPNUTÉ — len vygeneruje drafty, nič nepošle.</div>
              </div>
              <Switch checked={dryRun} onCheckedChange={setDryRun} data-testid="switch-dry-run" />
            </div>
          </Card>

          <Button
            onClick={run}
            disabled={busy || !prompt.trim() || !segmentCount}
            data-testid="btn-run-mass"
            className={`rounded-none px-8 py-6 text-xs uppercase tracking-[0.25em] ${
              dryRun
                ? "bg-fuchsia-500/20 border border-fuchsia-500/30 text-fuchsia-200 hover:bg-fuchsia-500/30"
                : "bg-rose-600 hover:bg-rose-500 text-white"
            }`}
          >
            {dryRun ? <Eye size={14} className="mr-2" /> : <Megaphone size={14} className="mr-2" />}
            {busy ? "..." : dryRun ? "Generuj náhľady" : `ODOSLAŤ ${segmentCount} SPRÁV`}
          </Button>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <Card>
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 mb-3">Audience</div>
            <div className="font-serif text-6xl tabular-nums text-emerald-400">{segmentCount}</div>
            <div className="text-xs text-white/40 mt-1">fanúšikov v tomto segmente</div>
          </Card>

          {result && (
            <Card>
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 mb-3">Výsledok</div>
              <div className="space-y-2 text-sm">
                <Row label="Kandidáti" value={result.candidates} />
                <Row label="Odoslané" value={result.sent} color="text-emerald-400" />
                <Row label="Zlyhali" value={result.failed} color="text-rose-400" />
              </div>
            </Card>
          )}

          {result?.drafts?.length > 0 && (
            <Card>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">
                {dryRun ? "Náhľady" : "Ukážka odoslaných"}
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {result.drafts.map((d, i) => {
                  const on = picked.has(d.fan_id);
                  return (
                    <button key={i} onClick={() => {
                        const next = new Set(picked);
                        on ? next.delete(d.fan_id) : next.add(d.fan_id);
                        setPicked(next);
                      }}
                      data-testid={`draft-${i}`}
                      className={`w-full text-left border-l-2 pl-3 py-2 text-xs transition-colors ${
                        on ? "border-fuchsia-500 bg-fuchsia-500/5" : "border-white/10 opacity-45 hover:opacity-80"
                      }`}>
                      <div className="flex items-center gap-2 text-[9px] uppercase tracking-widest text-white/40">
                        <span className={`inline-flex w-3.5 h-3.5 border items-center justify-center text-[9px] ${
                          on ? "border-fuchsia-400 bg-fuchsia-500 text-white" : "border-white/25"
                        }`}>{on ? "✓" : ""}</span>
                        {d.platform} · {d.fan_name || d.name}
                      </div>
                      <div className="font-serif italic text-white/85 mt-1 leading-relaxed">{d.text}</div>
                    </button>
                  );
                })}
                {result.drafts.length > 0 && (
                  <div className="flex items-center justify-between pt-3 border-t border-white/10 mt-2">
                    <button
                      onClick={() => setPicked(picked.size === result.drafts.length
                        ? new Set() : new Set(result.drafts.map((d) => d.fan_id)))}
                      className="text-[10px] uppercase tracking-widest text-white/40 hover:text-white/70">
                      {picked.size === result.drafts.length ? "Zrušiť výber" : "Vybrať všetky"}
                    </button>
                    <button onClick={sendPicked} disabled={sendingPicked || picked.size === 0}
                      data-testid="send-picked"
                      className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-40 transition-colors">
                      {sendingPicked ? "Odosielam…" : `Poslať vybrané (${picked.size})`}
                    </button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
      {!(result && result.drafts?.length > 0) && <Schedule />}
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-[rgb(18,20,28)] border border-white/5 p-5">{children}</div>;
}
function Row({ label, value, color = "" }) {
  return (
    <div className="flex justify-between">
      <span className="text-white/50">{label}</span>
      <span className={`tabular-nums ${color}`}>{value}</span>
    </div>
  );
}
